/**
 * 移动端音频自动播放解决方案（增强版）
 *
 * ── 问题 ──
 * 移动端浏览器要求 play() 必须在用户手势同步调用链内。
 * SSE 异步推送 TTS 时，手势已过期 → play() 被拦截。
 *
 * ── 核心策略 ──
 * ① AudioContext 长期存活（最关键）：
 *    用户手势中创建 AudioContext 并 resume()，之后在当前 AC 上
 *    的任何 BufferSource.start() 均不受手势限制。
 *    配合静默心跳保持 running 状态（iOS 后台挂起后自动恢复）。
 *
 * ② 页面级首次触摸解锁：
 *    不依赖 send 按钮的 unlock 时机，在用户首次触摸页面任意位置
 *    就执行解锁，确保后续所有 SSE 推送的 TTS 都能播放。
 *
 * ③ 微信环境 WeixinJSBridge 支持：
 *    微信内置浏览器有独立的事件机制，需专门适配。
 *
 * ④ Web Audio API 播放 TTS（autoPlay 主路径）：
 *    将 TTS 音频 URL 下载为 ArrayBuffer → AudioContext.decodeAudioData()
 *    → BufferSource.start()——完全依赖 AudioContext，不受 <audio> 元素限制。
 *
 * ⑤ HTMLAudioElement 共享实例（手动点击兜底）：
 *    用户点击"播报"按钮时，手势仍然存在，走原来逻辑。
 */

/** 一段极短的静默 WAV，用于手势中激活 Audio 元素 */
const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA";

// ════════════════════════════════════════════════════
//  AudioContext 共享实例（长期存活）
// ════════════════════════════════════════════════════

let _sharedCtx: AudioContext | null = null;
let _acUnlocked = false;
/** 静默振荡器节点，保持 AudioContext running 状态 */
let _heartbeatNode: OscillatorNode | null = null;
/** 音频缓存：URL → AudioBuffer，避免重复下载+解码 */
const _audioBufferCache = new Map<string, AudioBuffer>();
/** ★ 待播放队列：用户手势就绪后自动冲刷 */
interface PendingPlay {
  buffer: AudioBuffer;
  callbacks?: PlayTtsCallbacks;
}
let _pendingPlay: PendingPlay[] = [];

function getAudioContext(): AudioContext | null {
  if (_sharedCtx) {
    console.log("[audioPlayer] getAudioContext → 复用已有实例, state=" + _sharedCtx.state);
    return _sharedCtx;
  }

  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) {
      console.warn("[audioPlayer] getAudioContext → 浏览器不支持 AudioContext");
      return null;
    }
    _sharedCtx = new AC();
    console.log("[audioPlayer] getAudioContext → 创建新实例, state=" + _sharedCtx.state + ", sampleRate=" + _sharedCtx.sampleRate);
  } catch (e) {
    console.error("[audioPlayer] getAudioContext → 创建失败:", e);
    return null;
  }

  // 页面从后台恢复时自动 resume（iOS 会挂起 AudioContext）
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && _sharedCtx?.state === "suspended") {
      console.log("[audioPlayer] visibilitychange → 页面恢复，尝试 resume");
      _sharedCtx.resume()
        .then(() => console.log("[audioPlayer] visibilitychange → resume 成功, state=" + _sharedCtx!.state))
        .catch((e) => console.warn("[audioPlayer] visibilitychange → resume 失败:", e));
    }
  });

  return _sharedCtx;
}

/** 启动静默心跳：一个极低振幅的振荡器，防止 iOS 将 AudioContext 挂起 */
function startHeartbeat(ctx: AudioContext): void {
  if (_heartbeatNode) return;
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001; // 近乎无声
    osc.frequency.value = 20; // 低频，人耳完全听不到
    osc.type = "sine";
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    _heartbeatNode = osc;
  } catch {
    // 心跳失败不阻塞主流程
  }
}

/** 停止静默心跳（页面卸载时调用，但通常不需要） */
function stopHeartbeat(): void {
  try {
    _heartbeatNode?.stop();
  } catch {}
  _heartbeatNode = null;
}

/** ★ 冲刷待播放队列：AudioContext 成功 resume 后自动播放排队中的 TTS */
function flushPendingPlay(): void {
  if (_pendingPlay.length === 0) return;
  const queue = _pendingPlay;
  _pendingPlay = [];
  console.log("[audioPlayer] flushPendingPlay → 冲刷排队中的播放, 队列长度=" + queue.length);
  for (const item of queue) {
    doPlayBuffer(item.buffer, item.callbacks);
  }
}

/** ★ 实际执行 BufferSource.start() 的内部函数（playBuffer 和 flushPendingPlay 共用） */
function doPlayBuffer(
  buffer: AudioBuffer,
  callbacks?: PlayTtsCallbacks,
): AudioBufferSourceNode | null {
  const ctx = getAudioContext();
  if (!ctx) { callbacks?.onError?.(); return null; }

  console.log("[audioPlayer] playBuffer → ctx.state=" + ctx.state + ", _acUnlocked=" + _acUnlocked);

  if (ctx.state === "suspended") {
    // ★ _acUnlocked=false → 绝不可能 resume 成功（缺少用户手势）
    //    直接入队，不浪费尝试（Chrome 可能不 reject，导致 .catch() 不可靠）
    if (!_acUnlocked) {
      console.log("[audioPlayer] playBuffer → ctx=suspended 且未解锁，直接加入待播放队列");
      _pendingPlay.push({ buffer, callbacks });
      return null;
    }

    // _acUnlocked=true → 可能是 iOS 后台恢复挂起，尝试 resume
    console.warn("[audioPlayer] playBuffer → AudioContext 是 suspended (已解锁过), 尝试 resume...");
    ctx.resume()
      .then(() => {
        console.log("[audioPlayer] playBuffer → resume 成功, state=" + ctx.state);
        const node = doPlayBufferNow(ctx, buffer, callbacks);
        if (_pendingPlay.length > 0) {
          const queue = _pendingPlay;
          _pendingPlay = [];
          for (const item of queue) {
            doPlayBuffer(item.buffer, item.callbacks);
          }
        }
        return node;
      })
      .catch((e) => {
        console.warn("[audioPlayer] playBuffer → resume 失败:", e.name || e);
        _pendingPlay.push({ buffer, callbacks });
        console.log("[audioPlayer] playBuffer → 已加入待播放队列 (pendingPlay)");
      });
    return null;
  }

  return doPlayBufferNow(ctx, buffer, callbacks);
}

function doPlayBufferNow(
  ctx: AudioContext,
  buffer: AudioBuffer,
  callbacks?: PlayTtsCallbacks,
): AudioBufferSourceNode | null {
  try {
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    source.onended = () => {
      console.log("[audioPlayer] playBuffer → onended 触发");
      callbacks?.onEnded?.();
    };

    source.start(0);
    console.log("[audioPlayer] playBuffer → source.start(0) 已调用 ★");
    callbacks?.onPlay?.();
    return source;
  } catch (e: any) {
    console.error("[audioPlayer] playBuffer → 异常:", e?.message || e);
    callbacks?.onError?.();
    return null;
  }
}

// ════════════════════════════════════════════════════
//  共享 Audio 元素（手动按钮点击兜底）
// ════════════════════════════════════════════════════

let _sharedAudio: HTMLAudioElement | null = null;
let _elUnlocked = false;

// ════════════════════════════════════════════════════
//  核心 unlock 逻辑
// ════════════════════════════════════════════════════

/** 设备/环境检测 */
function isWeChat(): boolean {
  return /MicroMessenger/i.test(navigator.userAgent);
}

/**
 * 在用户手势中调用，解锁移动端音频自动播放。
 * 应在 send 按钮点击 / 语音录制开始 / 页面首次触摸时调用。
 */
export function unlockSharedAudio(): void {
  console.log("[audioPlayer] unlockSharedAudio 被调用, _acUnlocked=" + _acUnlocked + ", _elUnlocked=" + _elUnlocked);

  // ── ① AudioContext 全局解锁（iOS/Safari 最关键）──
  if (!_acUnlocked) {
    const ctx = getAudioContext();
    if (!ctx) {
      console.warn("[audioPlayer] unlock → AudioContext 不可用，跳过");
    } else if (ctx.state === "suspended") {
      console.log("[audioPlayer] unlock → AudioContext suspended，尝试 resume...");
      ctx.resume()
        .then(() => {
          _acUnlocked = true;
          startHeartbeat(ctx);
          console.log("[audioPlayer] unlock → AudioContext resume 成功，state=" + ctx.state);
          // ★ 冲刷排队中的 TTS 播放
          flushPendingPlay();
        })
        .catch((e) => {
          console.warn("[audioPlayer] unlock → AudioContext resume 失败:", e);
        });
    } else if (ctx.state === "running") {
      _acUnlocked = true;
      startHeartbeat(ctx);
      console.log("[audioPlayer] unlock → AudioContext 已在 running 状态, 直接标记");
      // ★ 冲刷排队中的 TTS 播放
      flushPendingPlay();
    } else {
      console.log("[audioPlayer] unlock → AudioContext state=" + ctx.state + " (closed?)");
    }
  } else {
    console.log("[audioPlayer] unlock → AudioContext 已解锁，跳过 resume");
    // ★ 冲刷排队中的 TTS 播放
    flushPendingPlay();
  }

  // ── ② HTMLAudioElement 元素级激活（兜底）──
  if (!_elUnlocked) {
    console.log("[audioPlayer] unlock → 尝试 HTMLAudioElement 激活...");
    const audio = new Audio(SILENT_WAV);
    _sharedAudio = audio;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        _elUnlocked = true;
        console.log("[audioPlayer] unlock → HTMLAudioElement 激活成功");
      })
      .catch((err) => {
        console.warn("[audioPlayer] unlock → HTMLAudioElement 激活失败:", err.name, err.message);
      });
  } else {
    console.log("[audioPlayer] unlock → HTMLAudioElement 已解锁，跳过");
  }
}

// ════════════════════════════════════════════════════
//  页面级首次触摸监听（最重要！）
// ════════════════════════════════════════════════════

let _pageListenerInstalled = false;

/**
 * 在 AiVoicePage 挂载时调用一次。
 * 监听页面上的首次 touchstart/click，提前解锁音频。
 * 这样即使 SSE 在 send 手势之前就推送了 TTS，也能正常播放。
 */
export function installPageTouchListener(): void {
  if (_pageListenerInstalled) {
    console.log("[audioPlayer] installPageTouchListener → 已安装，跳过");
    return;
  }
  _pageListenerInstalled = true;

  // WeChat 环境：优先监听 WeixinJSBridgeReady（在页面加载阶段就能解锁）
  if (isWeChat()) {
    console.log("[audioPlayer] installPageTouchListener → 检测到微信环境");
    const doUnlock = () => {
      console.log("[audioPlayer] WeixinJSBridge 触发解锁");
      unlockSharedAudio();
    };

    // WeixinJSBridge 可能已经就绪（如页面由 JSAPI 注入后重新加载）
    if (typeof (window as any).WeixinJSBridge === "object") {
      doUnlock();
    } else {
      document.addEventListener("WeixinJSBridgeReady", doUnlock, { once: true });
    }

    // 兜底：如果 WeixinJSBridgeReady 一直没触发，用 touchstart
    const fallback = () => {
      console.log("[audioPlayer] WeChat touchstart 兜底触发");
      unlockSharedAudio();
    };
    document.addEventListener("touchstart", fallback, { once: true });
  }

  // 非微信环境：首次触摸即解锁
  const unlockOnFirstTouch = () => {
    console.log("[audioPlayer] ★ 首次 touchstart 触发，开始解锁音频");
    unlockSharedAudio();
  };
  document.addEventListener("touchstart", unlockOnFirstTouch, { once: true, passive: true });

  // 桌面端兜底：首次点击即解锁
  const unlockOnFirstClick = () => {
    console.log("[audioPlayer] ★ 首次 click 触发，开始解锁音频");
    unlockSharedAudio();
  };
  document.addEventListener("click", unlockOnFirstClick, { once: true });

  console.log("[audioPlayer] installPageTouchListener → 监听已安装 (touchstart + click)");
}

/**
 * 检查是否已经解锁过（供外部判断是否需要显示提示）
 */
export function isAudioUnlocked(): boolean {
  return _acUnlocked || _elUnlocked;
}

/**
 * 将 data: URI 直接解码为 ArrayBuffer（无需网络请求）
 * 适用场景：后端推送 base64 音频数据时，跳过 fetch 直接解码
 */
function dataUriToArrayBuffer(dataUri: string): ArrayBuffer | null {
  try {
    const commaIdx = dataUri.indexOf(",");
    if (commaIdx === -1) return null;
    let base64 = dataUri.substring(commaIdx + 1);

    // ① URL-safe base64 → 标准 base64（- → +, _ → /）
    //    必须在 URL-decode 之前执行，因为 -/_ 是合法 URL 字符不会被 decode
    base64 = base64.replace(/-/g, "+").replace(/_/g, "/");

    // ② 尝试 URL-decode：后端可能在传输中将 base64 做了百分号编码
    //    例：+ → %2B, / → %2F, = → %3D
    try {
      const decoded = decodeURIComponent(base64);
      if (decoded !== base64) {
        base64 = decoded;
      }
    } catch {
      // URL-decode 失败（含非法 % 序列）→ 保持原值
    }

    // ③ ★ 终极清理：只保留合法 base64 字符 (A-Za-z0-9+/=)
    //    \s 正则无法匹配零宽空格(U+200B)、BOM(U+FEFF)等 Unicode 控制字符，
    //    必须用正向白名单确保 atob 不会遇到任何非法字符
    const cleaned = base64.replace(/[^A-Za-z0-9+/=]/g, "");
    if (base64.length !== cleaned.length) {
      console.warn(
        `[audioPlayer] base64 清理了 ${base64.length - cleaned.length} 个非法字符` +
        ` (原始长度=${base64.length}, 清理后=${cleaned.length})`
      );
    }
    base64 = cleaned;
    if (!base64) return null;

    // ④ 处理 = 出现在中间的情况（后端把 MP3 分成多块独立编码，每块末尾有 == 填充）
    //    直接传给 atob 会因中间的 = 而报 InvalidCharacterError
    //    方案：按 = 边界切分为独立 chunk → 分别解码 → 合并二进制数据
    const middleEq = base64.replace(/=+$/, "").indexOf("=");
    if (middleEq >= 0) {
      console.log(`[audioPlayer] 检测到多块拼接 base64，按 = 边界切分解码, middleEq=${middleEq}`);
      const buffers: ArrayBuffer[] = [];
      let chunkStart = 0;
      let i = 0;
      while (i < base64.length) {
        if (base64[i] === "=") {
          // 当前 chunk 的数据部分（不含 =）
          const chunkData = base64.substring(chunkStart, i);
          // 收集这个 chunk 的 padding（= 或 ==）
          let padding = "";
          while (i < base64.length && base64[i] === "=") {
            padding += "=";
            i++;
          }
          // 判断这些 = 是否是全局末尾 padding（后面没有更多数据了）
          const hasMoreData = i < base64.length;
          if (hasMoreData) {
            // chunk 边界：解码当前 chunk（携带其 padding）
            if (chunkData.length > 0) {
              const chunk = chunkData + padding;
              const bin = atob(chunk.length % 4 === 0 ? chunk : chunk + "=".repeat(4 - chunk.length % 4));
              const bytes = new Uint8Array(bin.length);
              for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
              buffers.push(bytes.buffer);
            }
            chunkStart = i;
          } else {
            // 全局末尾 padding：把 padding 放回当前 chunk
            i -= padding.length; // 回退，让后续统一处理
            break;
          }
        } else {
          i++;
        }
      }
      // 最后一个 chunk（含全局末尾 padding）
      if (chunkStart < base64.length) {
        let lastChunk = base64.substring(chunkStart);
        if (lastChunk.length > 0) {
          const pad = lastChunk.length % 4;
          if (pad > 0) lastChunk += "=".repeat(4 - pad);
          const bin = atob(lastChunk);
          const bytes = new Uint8Array(bin.length);
          for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
          buffers.push(bytes.buffer);
        }
      }
      // 合并所有 chunk
      if (buffers.length === 0) return null;
      const totalLen = buffers.reduce((s, b) => s + b.byteLength, 0);
      const merged = new Uint8Array(totalLen);
      let offset = 0;
      for (const buf of buffers) {
        merged.set(new Uint8Array(buf), offset);
        offset += buf.byteLength;
      }
      console.log(`[audioPlayer] 多块解码完成: ${buffers.length} 块, 合并后 ${totalLen} bytes`);
      return merged.buffer;
    }

    // ⑤ 无中间 = 的正常路径：填充 + 直接 atob
    const pad = base64.length % 4;
    if (pad > 0) {
      base64 += "=".repeat(4 - pad);
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    console.error("[audioPlayer] dataUriToArrayBuffer 失败:", e);
    // 打印部分 base64 用于排障
    try {
      const raw = dataUri.substring(dataUri.indexOf(",") + 1);
      // 找第一个非 base64 字符的位置
      const badCharMatch = raw.match(/[^A-Za-z0-9+/=\s\-_%]/);
      console.error(
        "[audioPlayer] base64 诊断: len=" + raw.length +
        ", len%4=" + (raw.replace(/[^A-Za-z0-9+/=]/g, "").length % 4) +
        (badCharMatch ? ", 首个非法字符位置=" + (badCharMatch.index ?? "?") + " charCode=" + badCharMatch[0].charCodeAt(0) : "") +
        ", 前200字符:", raw.substring(0, 200)
      );
      
      // ★ 二分法定位 atob 失败位置
      const cleaned2 = raw.replace(/[^A-Za-z0-9+/=]/g, "");
      let pad2 = cleaned2.length % 4;
      if (pad2 > 0) {
        const testStr = cleaned2 + "=".repeat(4 - pad2);
        // 尝试解码前半段
        const half = Math.floor(testStr.length / 4) * 4;
        try {
          atob(testStr.substring(0, half));
          console.log("[audioPlayer] 二分诊断: 前半段 atob OK, 问题在后半段");
        } catch {
          console.log("[audioPlayer] 二分诊断: 前半段 atob 也失败, 问题在前半段");
        }
        // 尝试只解码前 4000 字符
        const small = Math.min(4000, testStr.length);
        const smallAligned = Math.floor(small / 4) * 4;
        try {
          atob(testStr.substring(0, smallAligned));
          console.log(`[audioPlayer] 二分诊断: 前${smallAligned}字符 atob OK`);
        } catch {
          console.log(`[audioPlayer] 二分诊断: 前${smallAligned}字符 atob 也失败!`);
          // 逐千字符扫描
          for (let chunk = 4000; chunk <= 4000; chunk += 1000) {
            const aligned = Math.floor(chunk / 4) * 4;
            if (aligned > testStr.length) break;
            try {
              atob(testStr.substring(0, aligned));
            } catch {
              console.log(`[audioPlayer] atob 在第${aligned}个字符附近失败`);
              break;
            }
          }
        }
      }
    } catch {}
    return null;
  }
}

// ════════════════════════════════════════════════════
//  Web Audio API 播放 TTS（autoPlay 主路径）
// ════════════════════════════════════════════════════

/** 播放状态回调 */
export interface PlayTtsCallbacks {
  onLoadStart?: () => void;
  onCanPlay?: () => void;
  onPlay?: () => void;
  onEnded?: () => void;
  onPause?: () => void;
  onError?: () => void;
}

/**
 * 通过 Web Audio API 播放远程 TTS 音频。
 *
 * 优势：AudioContext 在用户手势中 resume 后，后续任何 BufferSource.start()
 *       都不再受手势限制，完美解决 SSE 异步场景的自动播放问题。
 *
 * @returns 清理函数，用于外部主动停止播放
 */
export function playTtsViaWebAudio(
  url: string,
  callbacks?: PlayTtsCallbacks,
): () => void {
  let cancelled = false;
  let sourceNode: AudioBufferSourceNode | null = null;

  console.log("[audioPlayer] playTtsViaWebAudio ★ 开始播放, url=" + url.substring(0, 80) + "...");
  callbacks?.onLoadStart?.();

  // 检查缓存
  const cached = _audioBufferCache.get(url);
  if (cached) {
    console.log("[audioPlayer] playTtsViaWebAudio → 命中缓存, duration=" + cached.duration + "s");
    callbacks?.onCanPlay?.();
    playBuffer(cached);
    return () => { cancelled = true; stopSource(); };
  }

  // ★ 如果是 data: URI，直接解析 base64 → ArrayBuffer，跳过 fetch
  //   避免了 fetch(data:...) 在移动端可能因长度/CSP导致的失败
  if (url.startsWith("data:")) {
    const ab = dataUriToArrayBuffer(url);
    if (!ab) {
      callbacks?.onError?.();
      return () => {};
    }
    if (cancelled) return () => {};
    console.log("[audioPlayer] playTtsViaWebAudio → data URI 直解成功, 大小=" + ab.byteLength + " bytes, 开始 decodeAudioData...");
    const ctx = getAudioContext();
    if (!ctx) {
      callbacks?.onError?.();
      return () => {};
    }
    ctx.decodeAudioData(ab.slice(0))
      .then((buffer) => {
        if (cancelled) return;
        console.log("[audioPlayer] playTtsViaWebAudio → 解码成功, duration=" + buffer.duration + "s");
        if (_audioBufferCache.size >= 20) {
          const firstKey = _audioBufferCache.keys().next().value;
          if (firstKey) _audioBufferCache.delete(firstKey);
        }
        _audioBufferCache.set(url, buffer);
        callbacks?.onCanPlay?.();
        playBuffer(buffer);
      })
      .catch((err) => {
        console.error("[audioPlayer] playTtsViaWebAudio ★ 解码失败:", err?.message || err);
        callbacks?.onError?.();
      });
    return () => { cancelled = true; stopSource(); };
  }

  // 下载 + 解码（远程 URL）
  const fetchStart = Date.now();
  console.log("[audioPlayer] playTtsViaWebAudio → 开始 fetch 下载...");
  fetch(url)
    .then((res) => {
      console.log("[audioPlayer] playTtsViaWebAudio → fetch 响应: status=" + res.status + ", ok=" + res.ok + ", type=" + res.type + ", 耗时=" + (Date.now() - fetchStart) + "ms");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.arrayBuffer();
    })
    .then((ab) => {
      if (cancelled) { console.log("[audioPlayer] playTtsViaWebAudio → 已取消，跳过解码"); return; }
      console.log("[audioPlayer] playTtsViaWebAudio → 下载完成, 大小=" + ab.byteLength + " bytes, 开始 decodeAudioData...");
      const ctx = getAudioContext();
      if (!ctx) throw new Error("AudioContext 不可用");
      return ctx.decodeAudioData(ab);
    })
    .then((buffer) => {
      if (cancelled || !buffer) { console.log("[audioPlayer] playTtsViaWebAudio → cancelled 或无 buffer, 跳过播放"); return; }
      console.log("[audioPlayer] playTtsViaWebAudio → 解码成功, duration=" + buffer.duration + "s, sampleRate=" + buffer.sampleRate + ", channels=" + buffer.numberOfChannels);
      // 缓存已解码的 AudioBuffer（最多缓存 20 条，超出清最早）
      if (_audioBufferCache.size >= 20) {
        const firstKey = _audioBufferCache.keys().next().value;
        if (firstKey) _audioBufferCache.delete(firstKey);
      }
      _audioBufferCache.set(url, buffer);

      callbacks?.onCanPlay?.();
      playBuffer(buffer);
    })
    .catch((err) => {
      if (!cancelled) {
        console.error("[audioPlayer] playTtsViaWebAudio ★ 播放失败:", err?.message || err, "url=" + url.substring(0, 80));
        callbacks?.onError?.();
      }
    });

  function playBuffer(buffer: AudioBuffer): void {
    if (cancelled) { console.log("[audioPlayer] playBuffer → 已取消"); return; }
    const node = doPlayBuffer(buffer, callbacks);
    if (node) sourceNode = node;
  }

  function stopSource(): void {
    try {
      if (sourceNode) {
        sourceNode.stop();
        console.log("[audioPlayer] stopSource → 已停止 sourceNode");
      }
    } catch {}
    sourceNode = null;
  }

  return () => {
    console.log("[audioPlayer] playTtsViaWebAudio → cleanup 被调用");
    cancelled = true;
    stopSource();
  };
}

/** 预加载 TTS 音频到 AudioBuffer 缓存（可选：在收到 TTS URL 后立即调用） */
export function preloadTtsAudio(url: string): void {
  if (_audioBufferCache.has(url)) return;
  fetch(url)
    .then((res) => res.arrayBuffer())
    .then((ab) => {
      const ctx = getAudioContext();
      if (!ctx) return;
      return ctx.decodeAudioData(ab);
    })
    .then((buffer) => {
      if (buffer) {
        if (_audioBufferCache.size >= 20) {
          const firstKey = _audioBufferCache.keys().next().value;
          if (firstKey) _audioBufferCache.delete(firstKey);
        }
        _audioBufferCache.set(url, buffer);
      }
    })
    .catch(() => {}); // 预加载失败不记录错误
}

/** 停止当前 Web Audio 播放（建议配合 playTtsViaWebAudio 返回的清理函数使用） */
export function stopWebAudioPlayback(): void {
  // 清理心跳（可选）
  // stopHeartbeat();
}

// ════════════════════════════════════════════════════
//  共享 Audio 元素（手动按钮点击 → 走此路径）
// ════════════════════════════════════════════════════

/**
 * 获取共享的 Audio 元素。
 * 复用时清理旧状态，保持已解锁元素的授权。
 *
 * 适用场景：用户手动点击"播报"按钮（手势仍在，直接用 HTMLAudioElement 即可）。
 */
export function getSharedAudio(): HTMLAudioElement {
  if (_sharedAudio) {
    const el = _sharedAudio;
    el.pause();
    el.src = "";
    el.currentTime = 0;
    el.oncanplaythrough = null;
    el.onerror = null;
    el.onended = null;
    el.onplay = null;
    el.onpause = null;
    return el;
  }

  _sharedAudio = new Audio();
  return _sharedAudio;
}
